// Static source catalogue for leads and deals.
//
// Values are stored lowercase in `lead.source` / `deal.source` to match what the
// Meta webhook writes (LeadSourcePlatform), so manually-created and
// webhook-captured leads group as one value.

export interface LeadSourceOption {
  value: string
  label: string
}

const SOURCE_META: Record<string, { label: string; color: string }> = {
  facebook: { label: 'Facebook', color: '#1877F2' },
  whatsapp: { label: 'WhatsApp', color: '#128C7E' },
  instagram: { label: 'Instagram', color: '#C13584' },
  google: { label: 'Google', color: '#DB4437' },
  website: { label: 'Website', color: '#4F46E5' },
  email: { label: 'Email', color: '#D97706' },
  phone_call: { label: 'Phone Call', color: '#64748B' },
  other: { label: 'Other', color: '#9CA3AF' },
  // Display-only: written by the lead-source webhook (LeadSourcePlatform), never
  // picked by a user, so they are not offered in the dropdown.
  tiktok: { label: 'TikTok', color: '#000000' },
  generic_webhook: { label: 'Webhook', color: '#9CA3AF' },
}

// The values a user can pick, in display order.
const SELECTABLE = [
  'facebook', 'whatsapp', 'instagram', 'google',
  'website', 'email', 'phone_call', 'other',
]

export const LEAD_SOURCE_OPTIONS: LeadSourceOption[] = SELECTABLE.map(value => ({
  value,
  label: SOURCE_META[value].label,
}))

const FALLBACK_COLOR = '#9CA3AF'

// Source has always been a free-text column, so stored values may be legacy
// ("Facebook") or arbitrary — match case-insensitively and degrade gracefully.
const normalize = (source: string) => source.trim().toLowerCase()

export function sourceColor(source: string): string {
  return SOURCE_META[normalize(source)]?.color ?? FALLBACK_COLOR
}

/** Unmapped values keep their text; snake_case ones are title-cased. */
export function sourceLabel(source: string): string {
  const known = SOURCE_META[normalize(source)]
  if (known) return known.label

  const trimmed = source.trim()
  if (!trimmed.includes('_')) return trimmed
  return trimmed
    .split('_')
    .filter(Boolean)
    .map(word => word[0].toUpperCase() + word.slice(1))
    .join(' ')
}
